import { CloseIcon, Dialog, ProgressSpinner, useGlobalToast, ZoomAndNavigatePdfPanel } from '@repo/atomic-components';
import { FC } from 'react';
import { RetrievePagesDialogVirtualizedList } from './RetrievePagesDialogVirtualizedList';

import { useRetrievePagesDialog } from './hooks';
import { RetrievePagesDialogThumbnailsList } from './RetrievePagesDialogThumbnailsList';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { GreenButton } from '@/ui/components/GreenButton';
import { isEndpointLockError } from '@/api';

interface RetrievePagesDialogProps {
  setVisible: (value: boolean) => void;
}

export const RetrievePagesDialog: FC<RetrievePagesDialogProps> = ({ setVisible }) => {
  const {
    isLoading,
    isRetrieving,
    bundleRetrievePageSelectedCheckboxes,
    pageZoomIn,
    pageZoomOut,
    navigatePageUp,
    navigatePageDown,
    handleRetrieveButtonClick,
  } = useRetrievePagesDialog();
  const globalToastRef = useGlobalToast();

  const handleHide = () => {
    setVisible(false);
  };

  const handleSave = async () => {
    try {
      await handleRetrieveButtonClick();
      setVisible(false);
    } catch (error) {
      // A lock refusal already has its own toast. Staying open keeps the ticked pages for a retry.
      if (isEndpointLockError(error)) return;
      globalToastRef.current?.show({
        severity: 'error',
        summary: 'Retrieve pages failed',
        detail: 'Please try again.',
        closable: true,
        life: 5000,
      });
    }
  };

  return (
    <>
      <Dialog
        visible={true}
        onHide={handleHide}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: '500px',
          width: '80vw',
          maxWidth: '80vw',
          minHeight: '400px',
          height: 'calc(100vh - 48px)',
          maxHeight: 'calc(100vh - 48px)',
          borderRadius: '8px',
          border: '1px solid #e1e1e0',
          overflow: 'hidden',
        }}
        contentClassName="!p-0"
        contentStyle={{ flex: 1, overflow: 'hidden' }}
        headerClassName="!hidden"
      >
        <div className="flex h-full w-full flex-col overflow-hidden">
          <div className="border-fletchers-gray-110 flex items-center justify-between border-b px-[24px] py-[16px]">
            <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">Retrieve Pages</div>
            <button
              type="button"
              aria-label="Close"
              onClick={handleHide}
              className="cursor-pointer rounded p-1 focus:outline-none"
            >
              <CloseIcon width={12} height={12} />
            </button>
          </div>
          <div className="flex flex-1 flex-col gap-[16px] overflow-hidden px-[24px] py-[16px]">
            <div className="text-[14px]/[16px] font-[400]">
              Select the pages you'd like to retrieve to the current page from the original document.
            </div>
            <div className="border-fletchers-gray-110 relative flex h-full w-full flex-row overflow-hidden rounded-[4px] border">
              {isLoading && (
                <div className="pointer-events-none absolute top-0 left-0 z-100 flex h-full w-full items-center justify-center">
                  <ProgressSpinner
                    style={{ width: '100px', height: '100px' }}
                    strokeWidth="4"
                    animationDuration="1.5s"
                  />
                </div>
              )}
              <div className="border-fletchers-gray-110 min-h-0 w-full flex-2 overflow-hidden border-r">
                <RetrievePagesDialogThumbnailsList />
              </div>
              <div className="relative min-h-0 w-full flex-10 overflow-hidden">
                <ZoomAndNavigatePdfPanel
                  zoomIn={pageZoomIn}
                  zoomOut={pageZoomOut}
                  pageUp={navigatePageUp}
                  pageDown={navigatePageDown}
                />
                <RetrievePagesDialogVirtualizedList />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 px-[24px] pb-[24px]">
            <OutlineButton className="!h-[32px]" onClick={handleHide}>
              Cancel
            </OutlineButton>
            <GreenButton
              className="!h-[32px]"
              disabled={bundleRetrievePageSelectedCheckboxes.length === 0 || isRetrieving}
              onClick={handleSave}
            >
              {isRetrieving
                ? 'Retrieving...'
                : `Retrieve ${bundleRetrievePageSelectedCheckboxes.length} ${bundleRetrievePageSelectedCheckboxes.length > 1 ? 'pages' : 'page'}`}
            </GreenButton>
          </div>
        </div>
      </Dialog>
    </>
  );
};
